import { Pagination } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import { useUserFeatures } from "../../../../components/contexts/UserFeaturesContext";
import DocumentList from "../../../../components/documents/DocumentList";
import DocumentsPage from "../DocumentsPage";
import SsoRedirectAccountList from "../../../../components/SsoRedirectAccountList";
import SignedUrlIframe from "../../../../components/SignedUrlIframe";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
};

type DocumentsPageContainerProps = {
  documentsJson: string;
  isHtmlJson: "true" | "false";
  sso: string;
  singleAccountMode?: string;
  supportsSsoPerAccount: string;
  supportsSsoTaxDocument: string;
  accountNamesAndNumbersJson: string;
  paginationCurrentPage?: string;
  paginationTotalPages?: string;
  paginationHasPrevious?: string;
  paginationHasNext?: string;
};

const DocumentsPageContainer = ({
  documentsJson,
  isHtmlJson,
  sso,
  singleAccountMode,
  supportsSsoPerAccount,
  supportsSsoTaxDocument,
  accountNamesAndNumbersJson,
  paginationCurrentPage,
  paginationTotalPages,
  paginationHasPrevious,
  paginationHasNext,
}: DocumentsPageContainerProps) => {
  const { sendNotification } = useNotificationContext();
  const features = useUserFeatures();
  const isSso = sso === "true";
  const isSsoPerAccount = supportsSsoPerAccount === "true";
  const enableDocumentFilters = features.enable_document_filters;
  const isSingleAccountMode = singleAccountMode === "true";

  const createPageUrl = (page: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", page.toString());
    return `?${params.toString()}`;
  };

  // Parse pagination props
  const pagination: PaginationProps | null =
    paginationCurrentPage && paginationTotalPages
      ? {
          currentPage: parseInt(paginationCurrentPage, 10),
          totalPages: parseInt(paginationTotalPages, 10),
          hasPrevious: paginationHasPrevious === "true",
          hasNext: paginationHasNext === "true",
        }
      : null;

  // SSO with per-account support
  if (isSso && isSsoPerAccount) {
    return (
      <div className="legacyGrid legacyGrid--centered legacyGrid--columnless legacyGrid-container">
        <div className="legacyGrid-item--shrink">
          <SsoRedirectAccountList
            accountNamesAndNumbersJson={accountNamesAndNumbersJson}
            supportsSsoTaxDocumentJson={supportsSsoTaxDocument}
          />
        </div>
      </div>
    );
  }

  // SSO iframe fallback
  if (isSso) {
    return (
      <div className="legacyGrid legacyGrid--centered legacyGrid--columnless legacyGrid-container">
        <SignedUrlIframe
          sendNotification={sendNotification}
          endpoint="signed_urls/documents/list"
          height="1000px"
        />
      </div>
    );
  }

  // Note that we'll be adding pagination support in:
  // https://linear.app/narmi/issue/CDB-3669/add-pdf-document-ui-pagination-support
  // Document filters feature enabled
  if (enableDocumentFilters) {
    return (
      <DocumentsPage
        documentsJson={documentsJson}
        isHtmlJson={isHtmlJson}
        singleAccountMode={isSingleAccountMode}
      />
    );
  }

  // Default: basic document list with optional pagination
  return (
    <div className="legacyGrid legacyGrid--centered legacyGrid--columnless legacyGrid-container">
      <div className="legacyGrid-item--shrink">
        <DocumentList documentsJson={documentsJson} isHtmlJson={isHtmlJson} />
      </div>
      {pagination && pagination.totalPages > 1 ? (
        <div className="legacyGrid-item--shrink">
          <Pagination
            totalPages={pagination.totalPages}
            defaultSelectedPage={pagination.currentPage}
            onPageChange={(newPage: number) => {
              window.location.href = createPageUrl(newPage);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default DocumentsPageContainer;
